import type {
  BulkScoutResponse,
  SortPersonIdsResponse,
  FavoriteRecruitments,
  ResponseCountScoutablePeople,
  ResponseIsFavorited,
  ResponseSearchScoutablePersonIds,
  ScoutUsageCounts,
} from '../types/scoutPage';
import type {
    ResponseRequest, ResponseIsScouted, ResponseFetchBookmarked,
    ResponseCreateOrDeleteBookmarked, ResponseFetchRecruitments
} from '../types/personShow';
import { reloadIfCsrfError, wrapFetch } from '../libs/fetch';
import type {
  SentAppliedStatus,
  SentHistoryItem,
  SentHistoryStatus,
  SentHistoryTab,
} from '../components/pages/scoutPage/types';

export const sendBulkScout = async (personIds: number[], csrfToken: string) => {
  return await wrapFetch<BulkScoutResponse>('/api/v1/scouts/bulk_create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ person_ids: personIds }),
    credentials: 'same-origin',
  });
};

export const postLike = async (personId: number, csrfToken: string) => {
  return await wrapFetch<ResponseRequest>(`/api/v1/scouts/post_like`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ person_id: personId }),
    credentials: 'same-origin',
  });
};

export const sortScoutablePersonIds = async (body: string, csrfToken: string) => {
  return await wrapFetch<SortPersonIdsResponse>('/api/v1/scouts/sort_scoutable_person_ids', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
    credentials: 'same-origin',
  });
};

export const fetchIsScouted = async (personId: number) => {
  return await wrapFetch<ResponseIsScouted>(`/api/v1/scouts/is_scouted/${personId}`);
};

export const fetchBookmarked = async (personId: number) => {
  return await wrapFetch<ResponseFetchBookmarked>(`/api/v1/scouts/get_bookmark/${personId}`);
};

export const createBookmarked = async (personId: number) => {
  return await wrapFetch<ResponseCreateOrDeleteBookmarked>(`/api/v1/scouts/create_bookmark/${personId}`);
};

export const deleteBookmarked = async (personId: number) => {
  return await wrapFetch<ResponseCreateOrDeleteBookmarked>(`/api/v1/scouts/delete_bookmark/${personId}`);
};

export const fetchScoutableRecruitments = async (personId: number) => {
  return await wrapFetch<ResponseFetchRecruitments>(`/api/v1/scouts/get_scoutable_recruitments/?id=${personId}`);
};

export const postScout = async (body: string, csrfToken: string) => {
  const response = await fetch('/api/v1/scouts', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
    credentials: 'same-origin',
  });

  if (await reloadIfCsrfError(response)) {
    return new Promise<Record<string, never>>(() => {});
  }

  const data = (await response.json()) as { errorMessage?: string };
  if (!response.ok) {
    throw new Error(data.errorMessage || 'スカウト送信でエラーが発生しました。');
  }

  return data;
};

export const fetchFavoriteRecruitments = async () => {
  return await wrapFetch<FavoriteRecruitments>('/api/v1/scouts/fetch_favorite_recruitments');
};

export const fetchSearchScoutablePersonIds = async (body: string, csrfToken: string) => {
  const response = await fetch('/api/v1/scouts/search_scoutable_person_ids', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
    credentials: 'same-origin',
  });
  if (await reloadIfCsrfError(response)) {
    return new Promise<ResponseSearchScoutablePersonIds>(() => {});
  }
  const data = await response.json();
  if (!response.ok) throw new Error(data.errorMessage || '検索に失敗しました。');
  return data as ResponseSearchScoutablePersonIds;
};

export const fetchCountScoutablePeople = async (body: string, csrfToken: string) => {
  const response = await wrapFetch<ResponseCountScoutablePeople>('/api/v1/scouts/count_scoutable_people', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
    credentials: 'same-origin',
  });

  if (!Number.isInteger(response.totalCount)) {
    throw new Error('候補者件数の取得でエラーが発生しました。');
  }

  return response;
};

export const fetchSentHistories = async (params: {
  searchBody: string;
  page: number;
  historyType: SentHistoryTab;
  readStatus: SentHistoryStatus;
  appliedStatus: SentAppliedStatus;
  sort: number;
}, csrfToken: string) => {
  const { searchBody, ...historyConditions } = params;
  const response = await fetch('/api/v1/scouts/sent_histories', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ ...JSON.parse(searchBody), ...historyConditions, scouts: ['sent'] }),
    credentials: 'same-origin',
  });
  if (await reloadIfCsrfError(response)) {
    return new Promise<{ histories: SentHistoryItem[]; totalCount: number }>(() => {});
  }
  const data = await response.json();
  if (!response.ok) throw new Error(data.errorMessage || '送信履歴の取得に失敗しました。');
  return data as { histories: SentHistoryItem[]; totalCount: number };
};

export const fetchFavorite = async (personId: number) => {
  return await wrapFetch<ResponseIsFavorited>(`/api/v1/scouts/get_favorite/${personId}`);
};

export type ScoutLikeHistoryResponse = {
  scout: {
    sent_count: number;
    read_count: number;
    entry_count: number;
    read_rate: number;
    entry_rate: number;
  };
  like: {
    sent_count: number;
    read_count: number;
    read_rate: number;
  };
};

export const fetchScoutLikeHistory = async (params: {
  scout_date_from?: string;
  scout_date_to?: string;
  like_date_from?: string;
  like_date_to?: string;
}) => {
  const searchParams = new URLSearchParams();
  if (params.scout_date_from) searchParams.append('scout_date_from', params.scout_date_from);
  if (params.scout_date_to) searchParams.append('scout_date_to', params.scout_date_to);
  if (params.like_date_from) searchParams.append('like_date_from', params.like_date_from);
  if (params.like_date_to) searchParams.append('like_date_to', params.like_date_to);

  return await wrapFetch<ScoutLikeHistoryResponse>(`/api/v1/scouts/history?${searchParams.toString()}`);
};

export const fetchScoutUsageCounts = async () => {
  return await wrapFetch<ScoutUsageCounts>('/api/v1/scouts/usage_counts');
};

export type ScoutDownloadCsvResponse = {
  data?: string;
  message?: string;
};

export const downloadScoutCsv = async (signal?: AbortSignal) => {
  return await wrapFetch<ScoutDownloadCsvResponse>('/api/v1/scouts/download_csv', { signal });
};
